import {
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ErrorHandler,
  InjectionToken,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  output,
  Signal,
  signal,
  SimpleChange,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { LocalStorageService } from '@core/common-legacy';

import { BoxComponent } from '../box/box.component';
import { DividerComponent } from '../divider/divider.component';
import { IconComponent } from '../icon/icon.component';

type UniqKey = string | null | undefined;

/**
 * This component serves as a container for collapsible content. It remembers its collapse state
 * in local storage and can be disabled from collapsing.
 *
 * Key ng-content slots:
 * - leftSlot: content on the left side of the header
 * - rightSlot: content on the right side of the header
 * - collapsible: collapsible content inside the card
 */

interface CollapsibleCardAnalytics {
  isCollapsed: Signal<boolean>;
}

export const COLLAPSIBLE_CARD_ANALYTICS = new InjectionToken<CollapsibleCardAnalytics>(
  'CollapsibleCardComponent',
);

@Component({
  selector: 'rt-collapsible-card',
  templateUrl: './collapsible-card.component.html',
  styleUrls: ['./collapsible-card.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, BoxComponent, DividerComponent],
  host: {
    '[class.header-divider]': 'hasHeaderDivider()',
    '[class.header-horizontal-padding]': 'hasHeaderHorizontalPadding()',
    '[class.collapsed]': 'isCollapsed()',
    '[class.expanded]': '!isCollapsed()',
    '[class.hoverable]': 'isHoverable()',
    '[class.collapse-disabled]': 'collapseDisabled()',
    '[class.no-border]': '!hasBorder()',
    '(mouseenter)': 'isHovered.set(true)',
    '(mouseleave)': 'isHovered.set(false)',
  },
  providers: [{ provide: COLLAPSIBLE_CARD_ANALYTICS, useExisting: CollapsibleCardComponent }],
})
export class CollapsibleCardComponent
  implements OnInit, OnChanges, OnDestroy, CollapsibleCardAnalytics
{
  private static readonly COLLAPSE_STATE_KEY = 'rt-collapsible-card';
  private static readonly COLLAPSE_KEY_REGISTER: Set<string> = new Set();

  /**
   * Unique key for the card to store its collapse state in local storage.
   * Ex.: job:<job-id>:candidate:<candidate-id>
   */
  public readonly uniqKey = input.required<UniqKey>();

  /**
   * Initial collapse state of the card. If not set, the component will be expanded by default.
   */
  public readonly collapsed = input<boolean, unknown>(false, { transform: booleanAttribute });

  public readonly forceCollapsed = input<boolean, unknown>(false, { transform: booleanAttribute });

  public readonly collapseDisabled = input<boolean, unknown>(false, {
    transform: booleanAttribute,
  });

  public readonly hasHeaderDivider = input<boolean, unknown>(false, {
    transform: booleanAttribute,
  });

  public readonly hasHeaderHorizontalPadding = input<boolean, unknown>(true, {
    transform: booleanAttribute,
  });

  public isHoverable = input(false, { transform: booleanAttribute });

  public hasBorder = input<boolean, boolean | string>(true, { transform: booleanAttribute });

  public hasRadius = input<boolean, boolean | string>(true, { transform: booleanAttribute });

  public hasBgAiSubdued = input<boolean, boolean | string>(false, { transform: booleanAttribute });

  public hasLeftSlotDesc = input(false, { transform: booleanAttribute });

  public collapseChanged = output<boolean>();

  @ViewChild(BoxComponent, { read: BoxComponent })
  public rtBox: BoxComponent;

  public isCollapsed = signal(false);

  public isHovered = signal(false);

  public collapseEffect = effect(() => {
    this.collapseChanged.emit(this.isCollapsed());
  });

  public rtBoxVariant = computed(() => {
    let resultVariant = 'no-padding vertical';

    if (!this.hasBorder()) {
      resultVariant += ' no-border';
    }

    if (!this.hasRadius()) {
      resultVariant += ' no-radius';
    }

    if (this.hasBgAiSubdued()) {
      resultVariant += ' background-ai-subdued';
    }

    return resultVariant;
  });

  constructor(
    private localStorageService: LocalStorageService,
    private cdRef: ChangeDetectorRef,
    private errorHandler: ErrorHandler,
  ) {}

  public ngOnInit(): void {
    this.setInitialState();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.updateUniqKey(changes?.uniqKey);
  }

  public ngOnDestroy(): void {
    this.unregisterUniqKey(this.uniqKey());
  }

  public onClick($event?: MouseEvent): void {
    if (this.collapseDisabled()) {
      $event?.stopPropagation();
      return;
    }

    this.isCollapsed.set(!this.isCollapsed());
    this.cdRef.detectChanges();
    this.saveCollapseState();
  }

  public saveCollapseState(): void {
    if (this.collapseDisabled() || !this.uniqKey()) {
      return;
    }

    this.localStorageService.set(this.buildUniqKey(), this.isCollapsed());
  }

  public toggle(): void {
    this.onClick();
  }

  private buildUniqKey(): string {
    return `${CollapsibleCardComponent.COLLAPSE_STATE_KEY}:${this.uniqKey()}`;
  }

  private validateAndRegisterUniqKey(): void | never {
    const uniqKey = this.uniqKey();
    if (this.collapseDisabled() || !uniqKey) {
      return;
    }

    if (CollapsibleCardComponent.COLLAPSE_KEY_REGISTER.has(uniqKey)) {
      this.errorHandler.handleError(new Error(`uniqKey "${uniqKey}" is not unique`));
    } else {
      CollapsibleCardComponent.COLLAPSE_KEY_REGISTER.add(uniqKey);
    }
  }

  private unregisterUniqKey(key: UniqKey): void {
    if (!key) {
      return;
    }

    CollapsibleCardComponent.COLLAPSE_KEY_REGISTER.delete(key);
  }

  private updateUniqKey(keyChange?: SimpleChange): void {
    if (
      !keyChange ||
      !keyChange.currentValue ||
      keyChange.currentValue === keyChange.previousValue
    ) {
      return;
    }

    this.unregisterUniqKey(keyChange.previousValue);
    this.validateAndRegisterUniqKey();
  }

  private setInitialState(): void {
    const forceCollapsed = this.forceCollapsed();
    const collapsed = this.collapsed();

    if (forceCollapsed) {
      this.isCollapsed.set(true);
      return;
    }

    this.isCollapsed.set(this.localStorageService.get(this.buildUniqKey(), collapsed));
  }
}
